import { useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import FormField from "components/MeForm/FormField";
import upStore from "store/upStore";
import { classNames } from "tools/ui";
import { isHexColor, toFullHex, contrastRatio } from "../helpers/colors";
import { SUGGESTED_COLORS, WCAG_AA_RATIO } from "../constants";

const WHITE = "#ffffff";
const BLACK = "#000000";

function ContrastChip({ color, textColor }) {
  const ratio = contrastRatio(color, textColor);
  const passes = ratio >= WCAG_AA_RATIO;
  return (
    <div
      className="flex flex-1 items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2"
      style={{ backgroundColor: color, color: textColor }}
    >
      <span className="text-sm font-semibold">Aa</span>
      <span className="text-xs">{ratio.toFixed(1)}:1</span>
      <span
        className={classNames(
          "rounded px-1.5 py-0.5 text-[10px] font-bold",
          passes ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
        )}
      >
        {passes ? "AA" : <FM id="melhous.dash.color.no_aa" />}
      </span>
    </div>
  );
}

function ColorPickerField(props) {
  const { field, attrs, data } = props;
  const { name, readOnly } = field;
  const { level } = attrs;
  const current = data.value || "";
  const [text, setText] = useState(current);
  const valid = !text || isHexColor(text);
  const color = isHexColor(current) ? current : null;

  useEffect(() => {
    setText(current);
  }, [current]);

  function commit(value) {
    if (readOnly) return;
    data.value = value;
    upStore(level, {
      fieldName: name,
      value: value,
      valueActive: value,
      field: field,
    });
  }

  function onTextChange(event) {
    const value = event.target.value.trim();
    setText(value);
    if (!value || isHexColor(value)) commit(value || null);
  }

  function onTextBlur() {
    if (!valid) setText(current);
  }

  function onPickerChange(event) {
    setText(event.target.value);
    commit(event.target.value);
  }

  return (
    <FormField {...field}>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label
            className="relative block size-12 shrink-0 cursor-pointer overflow-hidden rounded-md border border-slate-300"
            style={{ backgroundColor: color || WHITE }}
          >
            <input
              type="color"
              disabled={readOnly}
              value={toFullHex(current)}
              onChange={onPickerChange}
              className="absolute inset-0 size-full cursor-pointer opacity-0"
            />
          </label>
          <input
            id={name}
            name={name}
            type="text"
            value={text}
            disabled={readOnly}
            placeholder="#1f3a5f"
            maxLength={7}
            onChange={onTextChange}
            onBlur={onTextBlur}
            className={classNames(
              "h-12 w-full rounded-md border bg-white px-3 font-mono text-sm uppercase text-neutral-700 focus:outline-none",
              valid
                ? "border-slate-300 focus:border-cyan-500"
                : "border-red-400 focus:border-red-500",
            )}
          />
        </div>
        {!valid && (
          <p className="text-xs text-red-600">
            <FM id="melhous.dash.color.invalid" />
          </p>
        )}
        <div className="grid grid-cols-12 gap-1">
          {SUGGESTED_COLORS.map((suggested) => (
            <button
              key={suggested}
              type="button"
              title={suggested}
              disabled={readOnly}
              onClick={() => onPickerChange({ target: { value: suggested } })}
              className={classNames(
                "aspect-square w-full rounded border",
                toFullHex(current) === suggested && color
                  ? "border-cyan-500 ring-2 ring-cyan-500"
                  : "border-slate-300",
              )}
              style={{ backgroundColor: suggested }}
            />
          ))}
        </div>
        {color && (
          <div className="flex gap-2">
            <ContrastChip color={color} textColor={WHITE} />
            <ContrastChip color={color} textColor={BLACK} />
          </div>
        )}
      </div>
    </FormField>
  );
}

export default ColorPickerField;
